/**
 * Correcting one stored number.
 *
 * Extracted so that a value gets the same editing behaviour wherever it is
 * shown — the Details panel, and every component row in a breakdown. The
 * breakdown case is why: a computed cell's panel used to say "override the
 * value you mean" while its component rows offered no way to do it, and some of
 * those components (a split payroll's halves) are not grid rows anywhere, so the
 * instruction pointed at nothing at all.
 *
 * The three outcomes are all visible, because a save that silently does nothing
 * is the worst of them:
 *
 * - saved — the input clears; the caller reloads, and the number above it moves.
 * - conflict (409) — a newer extraction replaced the row. Said as news about the
 *   data, not as a failure, and the typed value is KEPT so it can be re-applied
 *   deliberately once the new number has been read. Never retried: a superseded
 *   row refuses the same write every time.
 * - failed — said plainly, with the value left unchanged.
 *
 * On an overridden row the editor also offers to REVERT: the extracted value
 * never left the row (that is the point of the override design), so undoing it
 * is one call, and the button says exactly which number it restores.
 */

import React, { useEffect, useState } from "react";

import type {
	KpiOverrideOutcome,
	KpiRevertOutcome,
} from "../../services/kpiService";
import type { KpiUnit } from "../../types/kpi";
import { formatKpiValueExact } from "./formatKpiValue";

export interface KpiOverrideControlProps {
	/** Public id of the stored row this writes to. */
	valueId: string;
	unit: KpiUnit;
	/** Compact form for a breakdown row; roomier in the panel. */
	size?: "default" | "compact";
	/** Shown above the field — whose number is being corrected. */
	label?: React.ReactNode;
	onOverride: (valueId: string, value: number) => Promise<KpiOverrideOutcome>;
	/** The extracted value this row would revert to (null on computed rows). */
	extractedValue?: number | null;
	/** Whether this row currently carries a human override. */
	isOverridden?: boolean;
	/** Undo the override, restoring the extracted value. */
	onRevert?: (valueId: string) => Promise<KpiRevertOutcome>;
}

const KpiOverrideControl: React.FC<KpiOverrideControlProps> = ({
	valueId,
	unit,
	size = "default",
	label,
	onOverride,
	extractedValue = null,
	isOverridden = false,
	onRevert,
}) => {
	const [draft, setDraft] = useState("");
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [conflict, setConflict] = useState(false);
	const [reverting, setReverting] = useState(false);
	const [revertError, setRevertError] = useState<string | null>(null);
	const [revertConflict, setRevertConflict] = useState(false);
	const compact = size === "compact";

	// A different row is a different subject: carrying a half-typed number across
	// is an easy way to write it onto the wrong value.
	useEffect(() => {
		setDraft("");
		setError(null);
		setConflict(false);
		setRevertError(null);
		setRevertConflict(false);
	}, [valueId]);

	const handleSave = async () => {
		const parsed = Number(draft.trim());
		if (draft.trim() === "" || Number.isNaN(parsed)) {
			setError("Enter a number.");
			return;
		}
		setSaving(true);
		setError(null);
		setConflict(false);
		const outcome = await onOverride(valueId, parsed);
		setSaving(false);
		if (outcome.kind === "saved") {
			setDraft("");
			return;
		}
		if (outcome.kind === "conflict") {
			setConflict(true);
			return;
		}
		setError("Could not save the override. The value is unchanged.");
	};

	const handleRevert = async () => {
		if (!onRevert) return;
		setReverting(true);
		setRevertError(null);
		setRevertConflict(false);
		const outcome = await onRevert(valueId);
		setReverting(false);
		if (outcome.kind === "saved") {
			// The caller reloads; the number above this control is the extracted
			// one again. The draft is cleared because there is nothing pending.
			setDraft("");
			return;
		}
		if (outcome.kind === "conflict") {
			setRevertConflict(true);
			return;
		}
		setRevertError("Could not revert the override. The value is unchanged.");
	};

	const canRevert =
		isOverridden &&
		extractedValue !== null &&
		extractedValue !== undefined &&
		onRevert !== undefined;

	const inputId = `kpi-override-${valueId}`;

	return (
		<div className={compact ? "mt-1" : "mt-2"}>
			{label && <div className="mb-1 text-xs text-text-muted">{label}</div>}
			<div className="flex gap-2">
				<label className="sr-only" htmlFor={inputId}>
					Override value
				</label>
				<input
					id={inputId}
					type="number"
					step="any"
					value={draft}
					onChange={(event) => setDraft(event.target.value)}
					placeholder={unit === "pct" ? "0.945" : "Corrected value"}
					className={`w-full rounded border border-gray-300 px-2 focus:border-[#008782] focus:outline-none ${
						compact ? "py-0.5 text-sm" : "py-1 text-base"
					}`}
				/>
				<button
					type="button"
					onClick={handleSave}
					disabled={saving || reverting}
					className={`shrink-0 rounded bg-[#008782] font-medium text-white hover:bg-[#004C4B] disabled:opacity-50 ${
						compact ? "px-2 py-0.5 text-xs" : "px-3 py-1 text-sm"
					}`}
				>
					{saving ? "Saving…" : "Save"}
				</button>
			</div>
			<p className={`mt-1 text-xs text-text-muted`}>
				{unit === "pct"
					? "Stored as a fraction — 0.945 is 94.5%."
					: "Replaces the extracted value; the source stays on record."}
			</p>
			{canRevert && (
				<button
					type="button"
					onClick={handleRevert}
					disabled={reverting || saving}
					className={`mt-1.5 shrink-0 rounded border border-gray-300 font-medium text-text-base hover:bg-gray-50 disabled:opacity-50 ${
						compact ? "px-2 py-0.5 text-xs" : "px-3 py-1 text-sm"
					}`}
				>
					{reverting
						? "Reverting…"
						: `Revert to extracted value (${formatKpiValueExact(
								extractedValue,
								unit,
						)})`}
				</button>
			)}
			{revertConflict && (
				<p
					className="mt-1 rounded bg-amber-50 px-2 py-1 text-xs text-amber-800"
					role="status"
				>
					This value is no longer overridden — review the current value
					before reverting.
				</p>
			)}
			{revertError && (
				<p className="mt-1 text-xs text-red-600" role="alert">
					{revertError}
				</p>
			)}
			{conflict && (
				<p
					className="mt-1 rounded bg-amber-50 px-2 py-1 text-xs text-amber-800"
					role="status"
				>
					This value was updated by a newer extraction — review the new value
					before overriding it.
				</p>
			)}
			{error && (
				<p className="mt-1 text-xs text-red-600" role="alert">
					{error}
				</p>
			)}
		</div>
	);
};

export default KpiOverrideControl;
